import { useEffect, useRef } from "react";
import { useStableCallback } from "./useStableCallback";

interface UseHoldActionOptions {
	/** Interval in milliseconds between repeated actions (default: 50ms) */
	interval?: number;
	/** Function to call on each interval while holding */
	onAction: (index: number, elapsedTime: number, deltaTime: number) => void;
	/** Function to call when hold starts */
	onStart?: () => void;
	/** Function to call when hold ends */
	onEnd?: () => void;
}

/**
 * Start loop of onAction during start and end. \
 * Can be used for long pressing a button. \
 * the onStart, onAction and onEnd should not be stable functions
 */

// Possible TODOs:
// - use option for RAF

export function useHoldAction(options: UseHoldActionOptions) {
	const { interval = 50, onAction, onStart, onEnd } = options;
	const holdingRef = useRef<
		| {
				intervalId: NodeJS.Timeout;
				startTime: number;
				lastTime: number;
				/** zero based */
				index: number;
		  }
		| undefined
	>(undefined);

	const startHold = useStableCallback(() => {
		if (holdingRef.current) return;

		const startTime = performance?.now?.() ?? Date.now();

		holdingRef.current = {
			intervalId: setInterval(() => {
				if (!holdingRef.current) return;

				const now = performance?.now?.() ?? Date.now();
				const duration = now - holdingRef.current.startTime;
				const dt = (now - holdingRef.current.lastTime) / 100;

				holdingRef.current.index += 1;
				holdingRef.current.lastTime = now;

				onAction(holdingRef.current.index, duration, dt);
			}, interval),
			index: 0,
			startTime,
			lastTime: startTime,
		};

		onStart?.();

		// Initial action
		onAction(0, 0, 0);
	});

	const stopHold = useStableCallback(() => {
		if (holdingRef.current) {
			clearInterval(holdingRef.current.intervalId);
			holdingRef.current = undefined;
		}
		onEnd?.();
	});

	// Automatically stop loop when component where this is used is unmounted
	useEffect(() => {
		return () => {
			stopHold();
		};
	}, [stopHold]);

	return {
		startHold,
		stopHold,
	};
}
